import React, { FC, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { palette } from 'entity/createTheme';
import HelpIcon from '@material-ui/icons/HelpOutline';
import Tooltip from '@material-ui/core/Tooltip';

interface InputProps {
  name?: string;
  placeholder?: string;
  autoFocus?: boolean;
  label?: string;
  error?: string;
  value: string;
  type?: string;
  isCustom?: boolean;
  width?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string | '';
  isShowDetail?: boolean;
  maxLength?: number;
  onBlur?: () => void;
  id?: string;
  tooltip?: string;
  isRequired?: boolean;
  hasError?: boolean;
}

const InputWrapper = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  & label,
  & input {
    color: ${palette.darkMedium2};
    font-size: 0.875rem;
    line-height: 1rem;
    -webkit-appearance: none;
  }
  & label {
    margin-bottom: 8px;
    font-size: 0.75rem;
    .icon {
      position: relative;
      top: 4px;
      left: 4px;
      path {
        color: ${palette.grayMedium10};
      }
    }
    .required {
      color: ${palette.redPrimary};
      margin-left: 4px;
      font-size: 0.625rem;
    }
  }
  & input {
    -webkit-text-fill-color: ${palette.darkMedium2};
    -webkit-opacity: 1;
    padding: 12px 10px;
    border: 1.5px solid ${palette.borderInput};
    border-radius: 6px;
    width: ${props => props.width || '240px'};
    background: ${palette.whitePrimary};
    font-size: 0.875rem;
    &:focus {
      outline: none;
      border-color: ${palette.deepBlueLight};
    }
    &::placeholder {
      color: ${palette.placeholder};
      -webkit-text-fill-color: ${palette.placeholder};
    }
  }
  & p {
    min-height: 42px;
    padding: 12px 10px;
    border: 1px solid ${palette.base};
    border-radius: 6px;
    font-size: 0.875rem;
    word-break: break-all;
    background-color: ${palette.base};
    color: ${palette.deepBlueDisable};
    line-height: 1rem;
  }
  &.error {
    & input {
      border-color: ${palette.redPrimary};
      &::placeholder {
        color: ${palette.redPrimary};
        -webkit-text-fill-color: ${palette.redPrimary};
      }
    }
    span.error_text {
      margin-top: 10px;
      color: ${palette.redPrimary};
      font-size: 13px;
    }
  }
  &.custom {
    & input {
      border: none;
      border-bottom: 1px solid ${palette.whitePrimary};
      border-radius: 0;
      &:focus {
        border-color: ${palette.deepBlueLight};
      }
      &:disabled {
        border-color: ${palette.whitePrimary};
      }
    }
    p {
      border-color: ${palette.whitePrimary};
    }
  }
`;

const InputText: FC<InputProps> = ({
  value,
  onChange,
  label,
  error,
  isCustom,
  width = '100%',
  className,
  isShowDetail,
  maxLength,
  tooltip,
  isRequired,
  hasError,
  ...rest
}) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const { t } = useTranslation();

  return (
    <InputWrapper
      className={`input-wrapper ${className} ${error || hasError ? 'error' : ''} ${
        isCustom ? 'custom' : ''
      }`}
      width={width}
    >
      {label && (
        <label>
          {label}
          {tooltip && (
            <span className="icon">
              <Tooltip title={tooltip}>
                <HelpIcon fontSize={'small'} />
              </Tooltip>
            </span>
          )}
          {isRequired && <span className="required">{t('common.requiredInput')}</span>}
        </label>
      )}

      {isShowDetail && <p>{value}</p>}
      {!isShowDetail && (
        <input
          value={value}
          required={isRequired}
          onChange={handleChange}
          {...rest}
          maxLength={maxLength}
        />
      )}
      {error && <span className="error_text">{error}</span>}
    </InputWrapper>
  );
};

export default InputText;
